import React, { FC, useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { v4 as uuidv4 } from 'uuid';

import {
  CloseIcon,
  CloudUploadIcon,
  InfoCircle,
  UploadedFileIcon,
  UploadIcon,
} from '../../../../../assets/icons/index';
import LoaderIcon from '../../../../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { FormTextarea } from '../../../../../components/textarea/index';
import { BackendRoute } from '../../../../../config/index';
import { useLocale } from '../../../../../hooks/index';
import { ApiService } from '../../../../../services/index';
import { ExerciseTypeTitle } from '../../../../../types/pool-plans.types';
import { ChildrenFormProps } from './terminated-exercise-form.types';
import { FormSchema, stepTwo as stepTwoFormSchema } from './terminated-exercise-form.validation';
export type StepTwoProps = ChildrenFormProps;

export const StepTwo: FC<StepTwoProps> = ({
  handleCloseModal,
  control,
  setFormData,
  nextFormStep,
  prevFormStep,
  formData,
  clearErrors,
  setError,
  errors: { stepTwo: errors },
  type,
  isLoading,
}) => {
  const { stepTwo } = useWatch<FormSchema>({ control });
  const { success } = stepTwoFormSchema.safeParse(stepTwo);
  const { messagesLocale } = useLocale();

  const files = stepTwo?.files || [];
  const isValid = success && (files.some(({ docLink }) => docLink) || files.length === 0);
  const uploadedFiles = files.reduce((prev, curr) => (curr.docLink ? prev + 1 : prev), 0);
  const isReachedUploadMaximum = uploadedFiles >= 20;
  const getStepTwoFilesData = useCallback(() => formData('stepTwo.files') || [], [formData]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach(({ file, errors }) => {
          errors.forEach((e) => {
            if (e.code === 'file-invalid-type') {
              setError('stepTwo.files', {
                type: 'manual',
                message: `${messagesLocale['INVALID_FILE_TYPE'].replace('{fileType}', file.name)}`,
              });
            }
          });
        });
        return;
      }

      if (isReachedUploadMaximum) return;
      const existingFiles = getStepTwoFilesData();
      const filesToUpload = acceptedFiles.slice(0, 20 - existingFiles.length);

      filesToUpload.forEach(async (file) => {
        const { callback, abort } = ApiService.uploadFile<string>();
        const id = uuidv4();
        const { error, success } = stepTwoFormSchema.safeParse({
          files: [{ doc: file, docLink: '', loadProgress: 1, abort, id }],
        });

        if (!success) return setError('stepTwo.files', error.issues[0]);

        if (success) clearErrors('stepTwo.files');

        const existingFiles = getStepTwoFilesData();

        setFormData(
          'stepTwo.files',
          [...existingFiles, { doc: file, docLink: '', loadProgress: 1, abort, id }],
          { shouldValidate: true },
        );

        const link = await callback(
          { endpoint: BackendRoute.SHARE_CLASSES, routePath: ['file'] },
          ['file', file],
          {
            onUploadProgress: (n) => {
              const currentFiles = getStepTwoFilesData();
              setFormData(
                'stepTwo.files',
                currentFiles.map((f) => (f.id === id ? { ...f, loadProgress: n } : f)),
              );
            },
            onAboard: () => {
              const currentFiles = getStepTwoFilesData();
              setFormData(
                'stepTwo.files',
                currentFiles.filter((f) => f.id !== id),
              );
            },
          },
        );

        if (!link) return;
        const currentFiles = getStepTwoFilesData();
        setFormData(
          'stepTwo.files',
          currentFiles.map((f) => (f.id === id ? { ...f, loadProgress: 100, docLink: link } : f)),
        );
      });
    },
    [
      clearErrors,
      getStepTwoFilesData,
      isReachedUploadMaximum,
      messagesLocale,
      setError,
      setFormData,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noClick: false,
    multiple: true,
    disabled: isReachedUploadMaximum,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xlsx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls'],
      'text/csv': ['.csv'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
  });

  const handleDeleteFile = useCallback(
    (id: string) => {
      const current = (formData('stepTwo.files') || []).find((file) => file.id === id);

      if (current && !current.docLink) current.abort();

      const currentData = (formData('stepTwo.files') || []).filter((file) => file.id !== id);
      setFormData('stepTwo.files', currentData);
      if (currentData.length === 0) return clearErrors('stepTwo.files');
    },
    [clearErrors, formData, setFormData],
  );

  return (
    <>
      <div className="invisible-y-scroll flex h-full w-[1309px] flex-col gap-4 bg-white p-6 pt-0">
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage id="DOCUMENTS_UPLOAD" />
        </span>
        <input
          {...getInputProps()}
          accept=".pdf, .doc, .docx, .xlsx, .xls, .csv, .png, .jpeg, .jpg"
          className="hidden"
          multiple
          type="file"
        />
        <label
          {...getRootProps()}
          className={twMerge(
            'relative flex w-full items-center gap-6 rounded-lg border border-dashed border-gray-200 p-6',
            isReachedUploadMaximum ? 'cursor-auto' : 'cursor-pointer',
          )}
        >
          {errors?.files ? <InfoCircle className="size-14" thinIconVariant /> : <CloudUploadIcon />}
          <div className="flex flex-col gap-2">
            <div className="flex">
              {errors?.files ? (
                <span className="text-sm font-[550] text-fireside-600">
                  {errors?.files.message?.toString()}
                </span>
              ) : (
                <p className="text-sm font-medium text-gray-700">
                  <span className="underline">
                    <AppFormattedMessage id="CLICK_TO_UPLOAD" />{' '}
                  </span>
                  <AppFormattedMessage id="OR_DRAG_AND_DROP" />
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1 text-label-sm font-[450] text-gray-500">
              <span>
                <AppFormattedMessage
                  id="SUPPORTED_FORMATS"
                  values={{
                    formats: 'doc/docx, xls/xlsx, csv, jpeg/jpg, png or pdf',
                  }}
                />
              </span>
              <span>
                <AppFormattedMessage id="MAXIMUM_FILE_SIZE" values={{ size: 10 }} />
              </span>
            </div>
            {errors?.files && (
              <span className="flex h-9 w-fit items-center gap-1 rounded border border-gray-100 bg-gray-25 px-3 py-[6px] text-sm font-[450] text-gray-700">
                <UploadIcon />
                <AppFormattedMessage id="UPLOAD_AGAIN" />
              </span>
            )}
          </div>
        </label>
        {(files?.length || 0) > 0 && (
          <div className="flex flex-col gap-2">
            <span className="pb-2 text-xs font-[450] text-gray-500">
              {uploadedFiles > 1 ? (
                <AppFormattedMessage id="COUNT_FILES_UPLOADED" values={{ count: uploadedFiles }} />
              ) : (
                <AppFormattedMessage id="ONE_FILE_UPLOADED" />
              )}
            </span>
            {files?.map(({ doc, docLink, loadProgress, id }, i) => (
              <div className="flex flex-col overflow-hidden rounded border border-brand-50 bg-brand-25">
                <div className="flex justify-between p-3" key={`${doc?.name}_${i}`}>
                  <div className="flex items-center gap-2 overflow-hidden">
                    {docLink ? <UploadedFileIcon /> : <LoaderIcon className="size-4" />}
                    <span className="truncate text-xs font-[450] text-gray-700">{doc?.name}</span>
                  </div>
                  <Button
                    className="w-fit p-1 hover:bg-gray-50"
                    onClick={() => handleDeleteFile(id || '')}
                    styleType="NONE"
                    type="button"
                  >
                    <CloseIcon className="size-2" />
                  </Button>
                </div>
                <div
                  className={twMerge(
                    'h-[2px] shrink-0 bg-brand-700 transition-all duration-700',
                    docLink && 'bg-transparent',
                  )}
                  style={{ width: (loadProgress || (docLink && 100)) + '%' }}
                />
              </div>
            ))}
          </div>
        )}
        <FormTextarea
          control={control}
          isShownCharCount={false}
          name="stepTwo.additionalNotes"
          placeholder={<AppFormattedMessage id="ADDITIONAL_NOTES_OPTIONAL" />}
          styleVariant="DEFAULT_W_PLACEHOLDER"
          textAreaClassName="min-h-[104px]"
          wrapperClassName="bg-gray-50"
        />
      </div>

      <div className="flex h-[44px] w-full justify-between gap-3">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="h-full w-[260px] p-3 text-sm font-[550] text-gray-25"
            disabled={!isValid}
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            {type && <AppFormattedMessage id={ExerciseTypeTitle[type]} />}
          </Button>
        </div>
      </div>
    </>
  );
};
